import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import type { Category, ChoreInstance, EventInstance, List, ListItem, Meal, Member, Weather } from '@pacer/shared';
import * as api from '../api';
import { EventEditor } from './EventEditor';
import { CheckToggle } from '../components/CheckToggle';
import { Countdowns } from '../components/Countdowns';
import { Icon, weatherIcon, type IconName } from '../components/Icon';
import { MessageBoard } from '../components/MessageBoard';
import { PinnedPlans } from '../components/PinnedPlans';
import { QuickCapture } from '../components/QuickCapture';
import { Scoreboard } from '../components/Scoreboard';
import { StatusCard } from '../components/StatusCard';
import { Avatar, Badge, Button, Card, cx } from '../components/ui';
import { choreSortKey, choreUrgency, fmtDueTime } from '../lib/chores';
import { daysSpanned, fmtDayLabel, fmtRange, useZone } from '../lib/dates';
import { groupByAisle } from '../lib/grocery';
import { eventCategory, eventColor } from '../lib/events';
import { confettiBig, confettiBurst, haptic } from '../state/celebrate';
import { useActiveMemberId } from '../state/activeMember';
import { useNow } from '../state/useNow';
import { queryKeys } from '../state/queries';
import { toast } from '../state/ui';

// Look a long way ahead so "Coming up" always shows what's actually next, even if the
// calendar is sparse and the next thing is months out (the server caps the range at 400
// days). The DISPLAY is capped by event count below, not by date.
const HORIZON_DAYS = 365;
const MAX_UPCOMING = 30;

/**
 * The family command centre. Organised by relevance, not by data type: a header band you read
 * from across the room, a prominent TODAY zone (events + tonight's dinner + today's chores
 * together), and a masonry of everything else that packs across the full width of a wall.
 *
 * Same component on a phone — it just collapses to one column, capture-first.
 */
export function Dashboard() {
  const zone = useZone();
  const now = useNow(1000);
  const [editing, setEditing] = useState<{ instance?: EventInstance } | null>(null);

  const today = DateTime.now().setZone(zone).startOf('day');
  const from = today.toUTC().toISO()!;
  const to = today.plus({ days: HORIZON_DAYS }).toUTC().toISO()!;
  const todayIso = today.toISODate()!;
  const mealTo = today.plus({ days: 2 }).toISODate()!;

  const { data: events = [], isLoading } = useQuery({
    queryKey: queryKeys.events(from, to, null),
    queryFn: () => api.getEvents(from, to, null),
  });
  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });
  const { data: categories = [] } = useQuery({ queryKey: queryKeys.categories, queryFn: api.getCategories });
  const { data: chores = [] } = useQuery({
    queryKey: [...queryKeys.chores, todayIso, todayIso],
    queryFn: () => api.getChores(todayIso, todayIso),
  });
  const { data: lists = [] } = useQuery({ queryKey: queryKeys.lists, queryFn: api.getLists });
  const { data: meals = [] } = useQuery({
    queryKey: [...queryKeys.meals, todayIso, mealTo],
    queryFn: () => api.getMeals(todayIso, mealTo),
  });
  const { data: weather } = useQuery({
    queryKey: queryKeys.weather,
    queryFn: api.getWeather,
    refetchInterval: 15 * 60 * 1000,
  });

  // Bucket events onto each day they touch (today onward). Today's already-finished timed
  // events drop off so the wall shows what's still ahead.
  const byDay = useMemo(() => {
    const buckets = new Map<string, EventInstance[]>();
    for (const ev of events) {
      for (const day of daysSpanned(ev, zone)) {
        if (day < todayIso) continue;
        if (day === todayIso && !ev.allDay && DateTime.fromISO(ev.endTs) < DateTime.now()) continue;
        buckets.set(day, [...(buckets.get(day) ?? []), ev]);
      }
    }
    return buckets;
  }, [events, zone, todayIso]);

  const todayEvents = byDay.get(todayIso) ?? [];

  // "Coming up" = the next MAX_UPCOMING event rows, in date order, however far out they fall.
  // Cap by count so a busy month doesn't render hundreds of rows, but a sparse calendar still
  // surfaces the next thing even if it's months away.
  const { cappedDays, overflow } = useMemo(() => {
    const ordered = [...byDay.entries()].filter(([d]) => d > todayIso).sort(([a], [b]) => a.localeCompare(b));
    const out: Array<[string, EventInstance[]]> = [];
    let shown = 0;
    let extra = 0;
    for (const [day, evs] of ordered) {
      const room = MAX_UPCOMING - shown;
      if (room <= 0) {
        extra += evs.length;
      } else if (evs.length <= room) {
        out.push([day, evs]);
        shown += evs.length;
      } else {
        out.push([day, evs.slice(0, room)]);
        extra += evs.length - room;
        shown = MAX_UPCOMING;
      }
    }
    return { cappedDays: out, overflow: extra };
  }, [byDay, todayIso]);

  const open = (ev: EventInstance): void => setEditing({ instance: ev });
  const shopping = lists.filter((l) => l.type === 'shopping');
  const todos = lists.filter((l) => l.type === 'todo');

  return (
    <div className="flex flex-col gap-4 px-4 py-5 md:px-8 md:py-7">
      {members.length === 0 && <FirstRun />}

      <HeaderBand now={now} zone={zone} weather={weather} />

      {/* Pinned plans — the big things the family is working toward, featured up top. */}
      <PinnedPlans />

      <div className="md:hidden">
        <QuickCapture />
      </div>

      {/* TODAY zone — the star, with the day's dinner + chores beside it. */}
      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr] lg:items-start">
        <TodayPanel
          today={today}
          events={todayEvents}
          loading={isLoading}
          zone={zone}
          members={members}
          categories={categories}
          onAdd={() => setEditing({})}
          onOpen={open}
        />
        <div className="flex flex-col gap-4">
          <MealsTile meals={meals} today={todayIso} zone={zone} />
          <TodayChores chores={chores} members={members} />
        </div>
      </div>

      {/* Everything else, packed into columns that fill the width instead of one tall rail. */}
      <div className="gap-4 sm:columns-2 xl:columns-3 [&>*]:mb-4 [&>*]:break-inside-avoid">
        <StatusCard />
        <Scoreboard />
        <ComingUp days={cappedDays} overflow={overflow} zone={zone} members={members} categories={categories} onOpen={open} />
        {shopping.map((l) => (
          <DashboardList key={l.id} list={l} kind="shopping" />
        ))}
        {todos.map((l) => (
          <DashboardList key={l.id} list={l} kind="todo" />
        ))}
        <MessageBoard />
        <Countdowns />
      </div>

      {editing && <EventEditor instance={editing.instance} onClose={() => setEditing(null)} />}
    </div>
  );
}

function FirstRun() {
  return (
    <Link
      to="/settings"
      className="flex items-center gap-4 rounded-lg border border-accent/40 bg-[var(--color-accent-fade)] px-5 py-4 transition-colors hover:bg-[var(--color-accent-dim)]"
    >
      <Icon name="people" size={22} className="shrink-0 text-accent" />
      <span className="flex-1">
        <span className="block font-display text-[17px] text-primary">Welcome to Pacer</span>
        <span className="text-[13px] text-secondary">
          Add your family so events, chores and lists can be shared. Tap here to start.
        </span>
      </span>
      <Icon name="chevron-right" size={16} className="shrink-0 text-accent" />
    </Link>
  );
}

function HeaderBand({ now, zone, weather }: { now: Date; zone: string; weather: Weather | null | undefined }) {
  const d = DateTime.fromJSDate(now).setZone(zone);
  return (
    <Card className="flex flex-col items-start justify-between gap-5 p-5 md:flex-row md:items-center md:gap-6 md:p-6">
      <div>
        <span className="eyebrow">{d.toFormat('cccc')}</span>
        <div className="tabular mt-1 flex items-baseline gap-2 font-mono leading-none font-medium text-primary">
          <span className="text-[15vw] sm:text-[52px] lg:text-[64px]">{d.toFormat('h:mm')}</span>
          <span className="text-[18px] text-muted lg:text-[22px]">{d.toFormat('a')}</span>
        </div>
        <p className="mt-1.5 font-display text-[17px] text-secondary lg:text-[20px]">
          {d.toFormat('LLLL d, yyyy')}
        </p>
      </div>
      {weather ? (
        <WeatherInline weather={weather} zone={zone} />
      ) : (
        <Link
          to="/settings"
          className="flex items-center gap-2 self-start rounded-md border border-dashed border-border-subtle px-3 py-2 text-muted transition-colors hover:border-border hover:text-secondary md:self-center"
        >
          <Icon name="cloud-sun" size={18} />
          <span className="text-[13px]">Add your location for weather</span>
        </Link>
      )}
    </Card>
  );
}

function WeatherInline({ weather, zone }: { weather: Weather; zone: string }) {
  const todayIso = DateTime.now().setZone(zone).toISODate();
  const days = weather.daily.filter((day) => day.date !== todayIso).slice(0, 4);
  return (
    <div className="flex items-center gap-5">
      <div className="flex items-center gap-3">
        <Icon
          name={weatherIcon(weather.now.code, weather.now.isDay)}
          size={44}
          strokeWidth={1.2}
          className="shrink-0 text-accent"
        />
        <div>
          <div className="tabular font-mono text-[32px] leading-none font-medium text-primary">
            {weather.now.tempF}°
          </div>
          <div className="text-[13px] text-secondary">{weather.now.label}</div>
        </div>
      </div>
      <div className="hidden gap-4 border-l border-border-subtle pl-5 sm:flex">
        {days.map((day) => (
          <div key={day.date} className="flex flex-col items-center gap-1" title={day.label}>
            <span className="eyebrow">{DateTime.fromISO(day.date, { zone }).toFormat('ccc')}</span>
            <Icon name={weatherIcon(day.code)} size={16} className="text-secondary" />
            <span className="tabular font-mono text-[12px] text-secondary">
              {day.highF}°<span className="text-muted"> {day.lowF}°</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TodayPanel({
  today,
  events,
  loading,
  zone,
  members,
  categories,
  onAdd,
  onOpen,
}: {
  today: DateTime;
  events: EventInstance[];
  loading: boolean;
  zone: string;
  members: Member[];
  categories: Category[];
  onAdd: () => void;
  onOpen: (e: EventInstance) => void;
}) {
  return (
    <Card className="flex flex-col overflow-hidden">
      <header className="flex items-center gap-3 border-b border-border-subtle px-4 py-3 md:px-5">
        <div>
          <span className="eyebrow">Today</span>
          <h2 className="font-display text-[20px] leading-tight text-primary">
            {today.toFormat('cccc, LLLL d')}
          </h2>
        </div>
        <Button variant="primary" icon="plus" className="ml-auto" onClick={onAdd}>
          Event
        </Button>
      </header>

      <div className="flex flex-col gap-1.5 p-3 md:p-4">
        {loading && <p className="px-1 font-mono text-[12px] text-muted">Loading…</p>}
        {!loading && events.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <Icon name="today" size={24} className="text-muted" />
            <p className="text-[14px] text-secondary">Nothing scheduled today.</p>
          </div>
        )}
        {events.map((ev) => (
          <EventRow key={ev.instanceId} ev={ev} zone={zone} members={members} categories={categories} onClick={() => onOpen(ev)} />
        ))}
      </div>
    </Card>
  );
}

function MealsTile({ meals, today, zone }: { meals: Meal[]; today: string; zone: string }) {
  const dinnerOn = (iso: string): Meal | undefined => meals.find((m) => m.date === iso && m.slot === 'dinner');
  const tonight = dinnerOn(today);
  const nextDinners = [1, 2]
    .map((off) => {
      const iso = DateTime.fromISO(today, { zone }).plus({ days: off }).toISODate()!;
      return { iso, meal: dinnerOn(iso) };
    })
    .filter((x) => x.meal);

  return (
    <Link to="/meals" className="block">
      <Card interactive className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <Icon name="meal" size={16} className="text-muted" />
          <span className="eyebrow">Dinner</span>
          <Icon name="chevron-right" size={13} className="ml-auto text-muted" />
        </div>

        {tonight ? (
          <div>
            <span className="eyebrow">Tonight</span>
            <p className="font-display text-[20px] leading-tight text-primary">{tonight.title}</p>
          </div>
        ) : (
          <p className="text-[14px] text-muted">No dinner planned tonight — tap to plan the week.</p>
        )}

        {nextDinners.length > 0 && (
          <div className="flex flex-col gap-1 border-t border-border-subtle pt-2.5">
            {nextDinners.map(({ iso, meal }) => (
              <div key={iso} className="flex items-baseline gap-2 text-[13px]">
                <span className="eyebrow w-10 shrink-0">{DateTime.fromISO(iso, { zone }).toFormat('ccc')}</span>
                <span className="truncate text-secondary">{meal!.title}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </Link>
  );
}

function ComingUp({
  days,
  overflow,
  zone,
  members,
  categories,
  onOpen,
}: {
  days: Array<[string, EventInstance[]]>;
  overflow: number;
  zone: string;
  members: Member[];
  categories: Category[];
  onOpen: (e: EventInstance) => void;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2 border-b border-border-subtle pb-3">
        <Icon name="calendar" size={16} className="text-muted" />
        <h2 className="font-display text-[20px] text-primary">Coming up</h2>
      </div>
      {days.length === 0 ? (
        <p className="font-mono text-[12px] text-muted">Nothing else on the calendar.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {days.map(([day, evs]) => (
            <div key={day} className="flex flex-col gap-1.5">
              <h3 className="eyebrow">{fmtDayLabel(DateTime.fromISO(day, { zone }), zone)}</h3>
              {evs.map((ev) => (
                <EventRow key={ev.instanceId} ev={ev} zone={zone} members={members} categories={categories} onClick={() => onOpen(ev)} />
              ))}
            </div>
          ))}
          {overflow > 0 && (
            <Link to="/calendar" className="mono px-1 py-1 font-mono text-[12px] text-muted hover:text-secondary">
              + {overflow} more on the calendar
            </Link>
          )}
        </div>
      )}
    </section>
  );
}

function EventRow({
  ev,
  zone,
  members,
  categories,
  onClick,
}: {
  ev: EventInstance;
  zone: string;
  members: Member[];
  categories: Category[];
  onClick: () => void;
}) {
  const attending = members.filter((m) => ev.memberIds.includes(m.id));
  const color = eventColor(ev, members, categories);
  const category = eventCategory(ev, categories);
  const live =
    !ev.allDay && DateTime.fromISO(ev.startTs) <= DateTime.now() && DateTime.fromISO(ev.endTs) >= DateTime.now();

  return (
    <button
      onClick={onClick}
      className={cx(
        'group flex items-center gap-3 rounded-lg border border-border-subtle border-l-2 bg-surface px-3 py-3 text-left',
        'transition-all duration-150 hover:border-border hover:bg-surface-high/60',
      )}
      style={{ borderLeftColor: color }}
    >
      <span className="tabular w-[100px] shrink-0 font-mono text-[12px] text-muted">{fmtRange(ev, zone)}</span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          {category?.icon && <Icon name={category.icon as IconName} size={13} className="shrink-0" style={{ color }} />}
          <span className="truncate text-[15px] text-primary">{ev.title}</span>
        </span>
        {(ev.location || ev.readOnly) && (
          <span className="block truncate font-mono text-[12px] text-muted">
            {[ev.location, ev.readOnly ? ev.feedName : null].filter(Boolean).join(' · ')}
          </span>
        )}
      </span>

      {live && <Badge tone="accent">Now</Badge>}
      {ev.source === 'agent' && <Badge tone="info">Exponent</Badge>}
      {ev.isRecurring && <Icon name="repeat" size={12} className="shrink-0 text-muted" />}

      <span className="flex -space-x-1.5">
        {attending.map((m) => (
          <Avatar key={m.id} name={m.name} color={m.color} photoUrl={m.avatarUrl} size={26} />
        ))}
      </span>
    </button>
  );
}

const URGENCY_CLASS = { overdue: 'text-danger', soon: 'text-warning', ok: 'text-muted' } as const;

function TodayChores({ chores, members }: { chores: ChoreInstance[]; members: Member[] }) {
  const qc = useQueryClient();
  const zone = useZone();
  const now = useNow(60_000);
  const nowDt = DateTime.fromJSDate(now).setZone(zone);
  const activeId = useActiveMemberId();

  const sorted = useMemo(
    () =>
      [...chores].sort(
        (a, b) =>
          choreSortKey(a.chore.dueTime).localeCompare(choreSortKey(b.chore.dueTime)) ||
          a.chore.title.localeCompare(b.chore.title),
      ),
    [chores],
  );

  const toggle = useMutation({
    mutationFn: (i: ChoreInstance) =>
      api.completeChore(i.chore.id, i.dueOn, !i.done, activeId ?? i.chore.assignedMemberId),
    onMutate: async (i) => {
      await qc.cancelQueries({ queryKey: queryKeys.chores });
      const prev = qc.getQueriesData<ChoreInstance[]>({ queryKey: queryKeys.chores });
      qc.setQueriesData<ChoreInstance[]>({ queryKey: queryKeys.chores }, (old) =>
        old?.map((x) => (x.chore.id === i.chore.id && x.dueOn === i.dueOn ? { ...x, done: !x.done } : x)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev?.forEach(([key, data]) => qc.setQueryData(key, data)),
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.chores }),
  });

  const remaining = chores.filter((c) => !c.done).length;

  // Big cheer the moment the last of today's chores is ticked off.
  const prevRemaining = useRef(remaining);
  useEffect(() => {
    if (prevRemaining.current > 0 && remaining === 0 && chores.length > 0) {
      confettiBig();
      toast('All chores done!', 'success');
    }
    prevRemaining.current = remaining;
  }, [remaining, chores.length]);

  if (chores.length === 0) return null;

  return (
    <Card className="flex flex-col overflow-hidden">
      <header className="flex items-center gap-2 border-b border-border-subtle px-4 py-3">
        <Icon name="chores" size={16} className="text-muted" />
        <h2 className="font-display text-[18px] text-primary">Chores</h2>
        <Badge tone={remaining === 0 ? 'success' : 'neutral'} className="ml-auto">
          {remaining === 0 ? 'All done' : `${remaining} left`}
        </Badge>
      </header>
      <ul>
        {sorted.map((i) => {
          const who = members.find((m) => m.id === i.chore.assignedMemberId);
          const urgency = choreUrgency(i.dueOn, i.chore.dueTime, zone, nowDt);
          return (
            <li key={i.chore.id} className="border-b border-border-subtle last:border-b-0">
              <button
                onClick={() => toggle.mutate(i)}
                className="touch flex w-full items-center gap-3 px-3 text-left transition-colors hover:bg-surface-high/40"
                aria-pressed={i.done}
              >
                <CheckToggle checked={i.done} size={20} onColor="success" color={who?.color ?? '#4caf7d'} />
                <span className={cx('flex-1 truncate text-[14px]', i.done ? 'text-muted line-through' : 'text-primary')}>
                  {i.chore.title}
                </span>
                {i.chore.dueTime && !i.done && (
                  <span className={cx('shrink-0 font-mono text-[12px]', URGENCY_CLASS[urgency])}>
                    {urgency === 'overdue' ? 'overdue' : `by ${fmtDueTime(i.chore.dueTime)}`}
                  </span>
                )}
                {i.chore.points > 0 && !i.done && <Badge tone="accent">{i.chore.points}</Badge>}
                {who && <Avatar name={who.name} color={who.color} photoUrl={who.avatarUrl} size={22} />}
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/**
 * Interactive list widget. This is where shopping actually happens: you're in the store, so
 * you tick items off right here as they go in the cart. Todos work the same, checked in place.
 * Only shows what's open; checking a shopping item marks it "got" and it drops off (reset it
 * back in the Lists view).
 */
function DashboardList({ list, kind }: { list: List; kind: 'shopping' | 'todo' }) {
  const qc = useQueryClient();
  const activeId = useActiveMemberId();
  const open = list.items.filter((i) => !i.done);

  const toggle = useMutation({
    mutationFn: (item: { id: string; done: boolean }) =>
      api.updateListItem(item.id, { done: !item.done, doneBy: activeId }),
    onMutate: async (item) => {
      await qc.cancelQueries({ queryKey: queryKeys.lists });
      const prev = qc.getQueryData<List[]>(queryKeys.lists);
      qc.setQueryData<List[]>(queryKeys.lists, (oldLists) =>
        oldLists?.map((l) =>
          l.id !== list.id ? l : { ...l, items: l.items.map((i) => (i.id === item.id ? { ...i, done: !i.done } : i)) },
        ),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(queryKeys.lists, ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.lists }),
  });

  if (open.length === 0) return null;

  const renderItem = (item: ListItem) => (
    <li key={item.id} className="border-b border-border-subtle last:border-b-0">
      <button
        onClick={(e) => {
          // The item vanishes on check, so the checkbox can't animate — fire the confetti at
          // the tap instead, and celebrate big if this was the last one.
          confettiBurst(e.clientX, e.clientY);
          haptic();
          if (open.length === 1) {
            confettiBig();
            toast(kind === 'shopping' ? 'Shopping done!' : 'To-do all done!', 'success');
          }
          toggle.mutate(item);
        }}
        className="touch flex w-full items-center gap-3 px-3 text-left transition-colors hover:bg-surface-high/40"
        aria-label={`${kind === 'shopping' ? 'Got' : 'Done'}: ${item.text}`}
      >
        <CheckToggle checked={false} size={20} />
        <span className="flex-1 truncate text-[14px] text-primary">{item.text}</span>
        {item.quantity > 1 && <span className="font-mono text-[13px] text-accent">×{item.quantity}</span>}
      </button>
    </li>
  );

  return (
    <Card className="flex flex-col overflow-hidden">
      <header className="flex items-center gap-2 border-b border-border-subtle px-4 py-3">
        <Icon name={kind === 'shopping' ? 'cart' : 'note'} size={16} className="text-muted" />
        <h2 className="font-display text-[18px] text-primary">{list.name}</h2>
        <Badge className="ml-auto">{open.length}</Badge>
      </header>
      {kind === 'shopping' ? (
        groupByAisle(open, (i) => i.text).map(({ aisle, items }) => (
          <div key={aisle.key}>
            <div className="border-b border-border-subtle bg-surface-high/40 px-4 py-1">
              <span className="eyebrow">{aisle.label}</span>
            </div>
            <ul>{items.map(renderItem)}</ul>
          </div>
        ))
      ) : (
        <ul>{open.map(renderItem)}</ul>
      )}
    </Card>
  );
}
